import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getDashboardPath } from '../../utils/roleUtils';
import { CircularProgress, Box } from '@mui/material';

/**
 * Route guard that checks if user has required role(s).
 * Redirects to role-appropriate dashboard if unauthorized.
 */
const RoleBasedRoute = ({ children, allowedRoles = [], fallback }) => {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }

  const userRoles = user?.roles || [];
  const legacyRole = user?.role;

  // If no specific roles required, allow access
  if (!allowedRoles || allowedRoles.length === 0) {
    return children;
  }

  // Check if the user has any of the allowed roles, honouring BOTH the RBAC
  // roles array and the legacy `user.role` field. (Many accounts only have the
  // legacy role set; checking RBAC roles alone caused an infinite redirect to
  // the same dashboard and a blank screen.)
  const hasAccess = allowedRoles.some(
    (role) => userRoles.includes(role) || role === legacyRole
  );

  if (!hasAccess) {
    const redirectPath = fallback || getDashboardPath(userRoles, legacyRole);
    // Guard against redirecting to the current path (which would loop and
    // render nothing). If they resolve to the same place, just show it.
    if (redirectPath === window.location.pathname) {
      return children;
    }
    return <Navigate to={redirectPath} replace />;
  }

  return children;
};

export default RoleBasedRoute;
